import styled from "@emotion/styled";
import { differenceInMinutes, format, parseISO } from "date-fns";
import { useStore } from "effector-react";
import {
  caseHistory as $caseHistory,
  CaseHistoryRecord,
  loadCaseHistory,
} from "Patient/Record";
import { memo, useEffect, useState } from "react";
import { Block, color, Flex, RecordHeartbeatIcon, Typography } from "ui-kit";
import { EmptyStateContainer, EmptyStateIcon } from "./EmptyState";
import { LoaderWithSpacing } from "./Loader";

type Props = {
  patientId: string;
};

type CaseHistoryRecordsSplitted = {
  current: CaseHistoryRecord[];
  past: CaseHistoryRecord[];
};

export const CaseHistoryPage = memo(({ patientId }: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const caseHistory = useStore($caseHistory);

  useEffect(() => {
    loadCaseHistory(patientId).finally(() => setIsLoading(false));
  }, [patientId]);

  const splittedRecords = caseHistory.reduce(
    (splittedRecords, record) => ({
      current:
        record.type === "current"
          ? [...splittedRecords.current, record]
          : splittedRecords.current,
      past:
        record.type === "past"
          ? [...splittedRecords.past, record]
          : splittedRecords.past,
    }),
    { current: [], past: [] } as CaseHistoryRecordsSplitted
  );

  if (isLoading) {
    return <LoaderWithSpacing />;
  }

  if (caseHistory.length === 0) {
    return (
      <EmptyStateContainer>
        <Flex justifyContent="center" marginBottom={4}>
          <EmptyStateIcon>
            <RecordHeartbeatIcon width="86px" height="86px" />
          </EmptyStateIcon>
        </Flex>
        <Block marginBottom={1}>
          <Typography fontColor="N300" type="h2">
            No case history
          </Typography>
        </Block>
        <Block>
          <Typography fontColor="N300" fontWeight={400} type="h3">
            Case history of the patient will be displayed here
          </Typography>
        </Block>
      </EmptyStateContainer>
    );
  }

  return (
    <Block marginTop={3} paddingLeft={5} paddingRight={5}>
      {splittedRecords.current.length > 0 && (
        <Block marginBottom={6}>
          <Block marginBottom={3}>
            <Typography as="h3" fontColor="N300" type="h2">
              Current
            </Typography>
          </Block>
          {splittedRecords.current.sort(newestOnTop).map((record) => (
            <CaseHistoryRecordContainer key={record.id}>
              <Block>
                <Block marginBottom={1}>
                  <Typography as="h4" type="h2">
                    {record.symptom}
                  </Typography>
                </Block>
                <Block marginBottom={1}>
                  <Typography fontColor="N300" type="h4">
                    Updated at:{" "}
                    {format(parseISO(record.updatedAt), "MM/dd/yyyy hh:mm a")}
                  </Typography>
                </Block>
                <Block>
                  <Typography fontWeight={400} type="h3">
                    {record.description}
                  </Typography>
                </Block>
              </Block>
            </CaseHistoryRecordContainer>
          ))}
        </Block>
      )}
      {splittedRecords.past.length > 0 && (
        <Block marginBottom={6}>
          <Block marginBottom={3}>
            <Typography as="h3" fontColor="N300" type="h2">
              Past
            </Typography>
          </Block>
          {splittedRecords.past.sort(newestOnTop).map((record) => (
            <CaseHistoryRecordContainer key={record.id}>
              <Block marginBottom={1}>
                <Typography as="h4" type="h2">
                  {record.symptom}
                </Typography>
              </Block>
              <Block marginBottom={1}>
                <Typography fontColor="N300" type="h4">
                  Period:{" "}
                  {record.periodFrom &&
                    format(parseISO(record.periodFrom), "MM/dd/yyyy")}
                  {record.periodFrom && record.periodTo && (
                    <>&nbsp;&mdash;&nbsp;</>
                  )}
                  {record.periodTo &&
                    format(parseISO(record.periodTo), "MM/dd/yyyy")}
                </Typography>
              </Block>
              <Block>
                <Typography fontWeight={400} type="h3">
                  {record.description}
                </Typography>
              </Block>
            </CaseHistoryRecordContainer>
          ))}
        </Block>
      )}
    </Block>
  );
});

function newestOnTop(case1: CaseHistoryRecord, case2: CaseHistoryRecord) {
  return differenceInMinutes(
    new Date(case2.periodTo || case2.periodFrom || case2.updatedAt),
    new Date(case1.periodTo || case1.periodFrom || case1.updatedAt)
  );
}

const CaseHistoryRecordContainer = styled.section`
  border-bottom: 1px solid ${color.N200};
  margin-bottom: 32px;
  padding-bottom: 32px;
  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;
